import { useEffect, useMemo, useRef, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { ulid } from 'ulid';
import { addDays, daysBetween, type Contact, type SchemaOptions, type User } from '@crmsy/shared';
import { todayIso } from '../lib/today.js';
import { db } from '../lib/db.js';
import { dispatch } from '../lib/actions.js';
import { PRIORITY, useKeys } from '../lib/keyboard.js';
import { matchProspect } from '@crmsy/shared';
import { toast } from '../lib/toast.js';
import { QUICK_DAYS } from '../components/DatePick.jsx';
import { useT, type Key } from '../i18n/index.js';
import { fetchExtract } from '../lib/api.js';
import { useExtractEnabled } from '../lib/features.js';
import type { ExtractResponse } from '@crmsy/shared';

const field = 'w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm outline-none focus:border-accent';

type Pick = 3 | 5 | 7 | 'custom' | 'none';
type ProposalKey = 'contactRole' | 'industry' | 'nextAction' | 'suggestedMessage';

/* Minimal Web Speech API shape (not in lib.dom for every TS target). */
interface Recognizer {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((ev: { resultIndex: number; results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }> }) => void) | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
  start(): void;
  stop(): void;
}

/** Web Speech API dictation (§6.1), EN/BG. Absent in Firefox: the button is hidden. */
function useDictation(onText: (t: string) => void) {
  const w = window as unknown as { SpeechRecognition?: new () => Recognizer; webkitSpeechRecognition?: new () => Recognizer };
  const Rec = w.SpeechRecognition ?? w.webkitSpeechRecognition;
  const [lang, setLang] = useState<'en-US' | 'bg-BG'>('en-US');
  const [on, setOn] = useState(false);
  const recRef = useRef<Recognizer | null>(null);
  const stop = () => {
    recRef.current?.stop();
    recRef.current = null;
    setOn(false);
  };
  const start = () => {
    if (!Rec) return;
    const rec = new Rec();
    rec.lang = lang;
    rec.continuous = true;
    rec.interimResults = false;
    rec.onresult = (ev) => {
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const r = ev.results[i];
        if (r && r.isFinal) onText(r[0]?.transcript ?? '');
      }
    };
    rec.onend = () => setOn(false);
    rec.onerror = () => setOn(false);
    rec.start();
    recRef.current = rec;
    setOn(true);
  };
  useEffect(() => () => recRef.current?.stop(), []);
  return { supported: !!Rec, on, lang, setLang, toggle: () => (on ? stop() : start()) };
}

/** New prospect (§6.1): instant, matches known prospects and flips to update mode. */
export function NewProspectView({ user }: { user: User }) {
  const t = useT();
  const contacts = useLiveQuery(() => db.contacts.toArray(), [], [] as Contact[]);
  const schemaRows = useLiveQuery(() => db.schema.toArray(), [], []);
  const schema = useMemo<SchemaOptions>(() => Object.fromEntries(schemaRows.map((r) => [r.property, r.options])), [schemaRows]);

  const [name, setName] = useState('');
  const [organization, setOrganization] = useState('');
  const [notes, setNotes] = useState('');
  const [leadSource, setLeadSource] = useState('');
  const [pick, setPick] = useState<Pick>(7);
  const [custom, setCustom] = useState(addDays(todayIso(), 14));
  /** A match the user dismissed with "Not them"; cleared when the lookup fields change. */
  const [ignored, setIgnored] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** §9: AI proposals shown as chips; `accepted` is per field; nothing is written until save. */
  const [proposals, setProposals] = useState<ExtractResponse | null>(null);
  const [chosen, setChosen] = useState<Record<ProposalKey, boolean>>({ contactRole: false, industry: false, nextAction: false, suggestedMessage: false });
  const [extracting, setExtracting] = useState(false);
  const extractEnabled = useExtractEnabled();
  const nameRef = useRef<HTMLInputElement>(null);
  const notesRef = useRef<HTMLTextAreaElement>(null);

  const dictation = useDictation((t) => setNotes((n) => (n ? `${n} ${t}` : t)));

  // The lookup never touches what is typed (D-22): the match is shown beside the form, and
  // the name / organization fields stay search text. Renaming happens in the panel.
  const match = useMemo(() => {
    const m = matchProspect(contacts, name, organization);
    return m && m.id !== ignored ? m : null;
  }, [contacts, name, organization, ignored]);

  useEffect(() => {
    nameRef.current?.focus();
  }, []);
  useKeys(
    {
      n: () => nameRef.current?.focus(),
      '1': () => nameRef.current?.focus(),
      // Ctrl+Enter saves from anywhere on the form, e.g. with a proposal chip focused.
      'Ctrl+Enter': () => void submit(),
      'Meta+Enter': () => void submit(),
    },
    { priority: PRIORITY.view },
  );

  const today = todayIso();
  const date = pick === 'none' ? null : pick === 'custom' ? custom : addDays(today, pick);
  // In update mode the known prospect already has a name; only the notes are required.
  const valid = (match !== null || name.trim().length > 0) && notes.trim().length > 0 && (pick !== 'custom' || /^\d{4}-\d{2}-\d{2}$/.test(custom));

  const reset = () => {
    setName('');
    setOrganization('');
    setNotes('');
    setLeadSource('');
    setPick(7);
    setIgnored(null);
    setProposals(null);
    nameRef.current?.focus();
  };

  const extract = async () => {
    if (!notes.trim() || extracting) return;
    setExtracting(true);
    try {
      const r = await fetchExtract({ company: organization.trim() || match?.companyName || '', contact: name.trim() || match?.contact || '', notes: notes.trim(), contactId: match?.id });
      setProposals(r);
      // Confident, known values start accepted; a value outside Notion's list needs an explicit click (D-14).
      setChosen({
        contactRole: !!r.contactRole.value && !r.contactRole.isNew && r.contactRole.confidence >= 0.5,
        industry: !!r.industry.value && !r.industry.isNew && r.industry.confidence >= 0.5,
        nextAction: !!r.nextAction.value && !r.nextAction.isNew && r.nextAction.confidence >= 0.5,
        suggestedMessage: !!r.suggestedMessage.value && r.suggestedMessage.confidence >= 0.5,
      });
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), { kind: 'error' });
    } finally {
      setExtracting(false);
    }
  };

  /** Accepted proposals as action fields, plus the confirmed new options for the log (D-14). */
  const acceptedFields = () => {
    const out: { contactRole?: string; industry?: string; nextAction?: string; newOptions: { property: string; value: string }[] } = { newOptions: [] };
    if (!proposals) return out;
    const take = (k: 'contactRole' | 'industry' | 'nextAction', property: string) => {
      const p = proposals[k];
      if (chosen[k] && p.value) {
        out[k] = p.value;
        if (p.isNew) out.newOptions.push({ property, value: p.value });
      }
    };
    take('contactRole', 'Contact Role');
    take('industry', 'Industry');
    take('nextAction', 'Next Action');
    return out;
  };

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    try {
      const extra = acceptedFields();
      const suggestion = proposals && chosen.suggestedMessage ? proposals.suggestedMessage.value : null;
      if (match) {
        await dispatch('updateProspect', { contactId: match.id, notes: notes.trim(), nextContactDate: date, ...extra });
        if (suggestion) await dispatch('addSuggestion', { contactId: match.id, text: suggestion });
        toast(t('np.updateAdded', { name: match.companyName || match.contact }));
      } else {
        const tempId = ulid();
        await dispatch('createProspect', { tempId, name: name.trim(), organization: organization.trim(), notes: notes.trim(), leadSource: leadSource || null, nextContactDate: date, ...extra });
        if (suggestion) await dispatch('addSuggestion', { contactId: tempId, text: suggestion });
        toast(t('np.added', { name: organization.trim() || name.trim() }));
      }
      reset();
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), { kind: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      void submit();
    }
  };

  /** Accept the suggested contact: fill Name / Organization with its details and go to Notes. */
  const accepted = !!match && name === (match.contact || match.companyName) && organization === match.companyName;
  const acceptMatch = () => {
    if (!match) return;
    setName(match.contact || match.companyName);
    setOrganization(match.companyName);
    notesRef.current?.focus();
  };
  const onLookupKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Tab' && !e.shiftKey && match && !accepted) {
      e.preventDefault();
      acceptMatch();
      return;
    }
    onKeyDown(e);
  };

  const clearable = (value: string, set: (v: string) => void, props: React.InputHTMLAttributes<HTMLInputElement>, ref?: React.RefObject<HTMLInputElement | null>) => (
    <div className="relative">
      <input
        ref={ref}
        value={value}
        onChange={(e) => {
          set(e.target.value);
          setIgnored(null);
        }}
        onKeyDown={onLookupKeyDown}
        className={`${field} pr-8`}
        {...props}
      />
      {value && (
        <button
          type="button"
          aria-label={t('np.clear')}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted hover:text-ink"
          onClick={() => {
            set('');
            setIgnored(null);
            nameRef.current?.focus();
          }}
        >
          ✕
        </button>
      )}
    </div>
  );

  return (
    <div className="h-full overflow-y-auto p-3 md:p-6">
      <form
        className="mx-auto max-w-xl"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <h1 className="text-lg font-semibold max-md:text-base">{match ? t('np.known') : t('np.title')}</h1>
        {match ? (
          <div className="mt-2 flex items-center gap-2 rounded-lg border border-accent bg-accent-soft px-3 py-2 text-sm text-accent-dark">
            <span className="flex-1">
              <b>{match.companyName || match.contact}</b>
              {match.contact && match.companyName ? ` · ${match.contact}` : ''}
              {match.contactRole ? ` (${match.contactRole})` : ''} {t('np.update')}
              {match.nextContactDate && <> {t('np.nextContact', { date: match.nextContactDate, n: daysBetween(today, match.nextContactDate) })}</>}
            </span>
            {!accepted && (
              <button type="button" className="btn" onClick={acceptMatch} title={t('np.useTitle')}>
                {t('np.use')} <kbd>Tab</kbd>
              </button>
            )}
            <button type="button" className="btn" onClick={() => setIgnored(match.id)}>
              {t('np.notThem')}
            </button>
          </div>
        ) : (
          <p className="desktop-only mt-1 text-sm text-muted">{t('np.defaults')}</p>
        )}

        <label className="mt-3 block">
          <span className="text-xs font-medium text-muted">{t('np.name')}</span>
          {clearable(name, setName, { placeholder: t('np.namePlaceholder'), autoComplete: 'off', name: 'name' }, nameRef)}
        </label>
        <label className="mt-3 block">
          <span className="text-xs font-medium text-muted">{t('np.org')}</span>
          {clearable(organization, setOrganization, { placeholder: t('np.orgPlaceholder'), autoComplete: 'off', name: 'organization' })}
        </label>
        <label className="mt-3 block">
          <span className="flex items-center justify-between text-xs font-medium text-muted">
            <span>{t('np.notes')}</span>
            {dictation.supported && (
              <span className="flex items-center gap-1">
                <button type="button" className={`chip ${dictation.on ? 'border-danger text-danger' : ''}`} onClick={dictation.toggle}>
                  {dictation.on ? t('np.stop') : t('np.dictate')}
                </button>
                <button type="button" className="chip" onClick={() => dictation.setLang(dictation.lang === 'en-US' ? 'bg-BG' : 'en-US')}>
                  {dictation.lang === 'en-US' ? 'EN' : 'BG'}
                </button>
              </span>
            )}
          </span>
          <textarea ref={notesRef} rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} onKeyDown={onKeyDown} placeholder={t('np.notesPlaceholder')} name="notes" className={`${field} mt-1`} />
        </label>
        {extractEnabled && (
          <div className="mt-2">
            <button type="button" className="btn" onClick={() => void extract()} disabled={!notes.trim() || extracting}>
              {extracting ? t('np.extracting') : t('np.extract')}
            </button>
          </div>
        )}
        {proposals && (
          <div className="mt-2 rounded-lg border border-line bg-surface px-3 py-2" data-proposals>
            <p className="text-[11px] text-muted">{t('np.proposals')}</p>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {(['contactRole', 'industry', 'nextAction'] as const).map((k) => {
                const p = proposals[k];
                if (!p.value) return null;
                return (
                  <button
                    key={k}
                    type="button"
                    role="checkbox"
                    aria-checked={chosen[k]}
                    className={`chip ${p.isNew ? 'border-dashed' : ''}`}
                    data-on={chosen[k]}
                    data-proposal={k}
                    title={`${Math.round(p.confidence * 100)}%`}
                    onClick={() => setChosen((a) => ({ ...a, [k]: !a[k] }))}
                  >
                    {t(`np.proposal.${k}` as Key)}: {p.value}
                    {p.isNew && <span className="rounded-full bg-warn-soft px-1.5 text-[10px] text-warn-ink">{t('np.newOption')}</span>}
                  </button>
                );
              })}
            </div>
            {proposals.suggestedMessage.value && (
              <div className="mt-2 flex items-start gap-2 text-sm">
                <button type="button" role="checkbox" aria-checked={chosen.suggestedMessage} className="chip shrink-0" data-on={chosen.suggestedMessage} data-proposal="suggestedMessage" onClick={() => setChosen((a) => ({ ...a, suggestedMessage: !a.suggestedMessage }))}>
                  ✨ {t('np.suggestedAccept')}
                </button>
                <p className="whitespace-pre-wrap text-xs text-muted">{proposals.suggestedMessage.value}</p>
              </div>
            )}
          </div>
        )}
        {!match && (
          <label className="mt-3 block">
            <span className="text-xs font-medium text-muted">{t('np.leadSource')}</span>
            <select value={leadSource} onChange={(e) => setLeadSource(e.target.value)} className={`${field} mt-1`} name="leadSource">
              <option value="">—</option>
              {(schema['Lead Source'] ?? []).map((o) => (
                <option key={o.name} value={o.name}>{o.name}</option>
              ))}
            </select>
          </label>
        )}

        <div className="mt-3 text-xs font-medium text-muted">{t('np.nextFollowUp')}</div>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {QUICK_DAYS.map((d) => (
            <button key={d} type="button" className="chip" data-on={pick === d} onClick={() => setPick(d)}>
              {t('date.days', { n: d })}
            </button>
          ))}
          <button type="button" className="chip" data-on={pick === 'custom'} onClick={() => setPick('custom')}>{t('date.custom')}</button>
          <button type="button" className="chip" data-on={pick === 'none'} onClick={() => setPick('none')}>✕ {t('date.none')}</button>
          <span className="self-center text-[11px] text-muted">{date ? `→ ${date}` : t('date.noDate')}</span>
        </div>
        {pick === 'custom' && <input type="date" value={custom} onChange={(e) => setCustom(e.target.value)} className={`${field} mt-2`} />}

        <div className="mt-5 flex items-center gap-2">
          <button type="submit" className="btn btn-primary shrink-0 whitespace-nowrap" disabled={!valid || busy}>
            {match ? t('np.addUpdate') : t('np.add')}
          </button>
          <span className="desktop-only text-[11px] text-muted"><kbd>Ctrl+Enter</kbd> {t('np.hint')}</span>
        </div>
        {!user.notionUserId && <p className="mt-3 text-xs text-danger">{t('np.unmapped')}</p>}
      </form>
    </div>
  );
}
